import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ukSchedulingAPI, employeeAPI, organizationAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ItemTypes = {
  SHIFT: 'shift',
  EMPLOYEE: 'employee'
};

const DragDropShiftCalendar = ({ 
  viewType = 'week', 
  onShiftUpdate,
  onConflictDetected,
  filters = {}
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    department: '',
    site: '',
    role: '',
    employee: '',
    showOvertime: false,
    showConflicts: false
  });

  const { user, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCalendarData();
    fetchReferenceData();
  }, [currentDate, viewType, activeFilters]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      
      const startDate = getCalendarStartDate();
      const endDate = getCalendarEndDate();
      
      const response = await ukSchedulingAPI.getShifts({
        date_from: startDate.toISOString(),
        date_to: endDate.toISOString(),
        ...activeFilters
      });
      
      setShifts(response.data.shifts || []);
      
      // Detect conflicts
      const detectedConflicts = detectShiftConflicts(response.data.shifts || []);
      setConflicts(detectedConflicts);
      
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [employeesRes, sitesRes] = await Promise.all([
        employeeAPI.getAll({ active: true }),
        organizationAPI.getCompanies() // Sites are nested in companies
      ]);
      
      setEmployees(employeesRes.data.employees || []);
      
      // Extract sites from companies
      const allSites = employeesRes.data.companies?.reduce((acc, company) => {
        return acc.concat(company.sites || []);
      }, []) || [];
      setSites(allSites);
      
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const getCalendarStartDate = () => {
    const start = new Date(currentDate);
    if (viewType === 'week') {
      start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    } else if (viewType === 'month') {
      start.setDate(1);
      start.setDate(start.getDate() - start.getDay());
    }
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getCalendarEndDate = () => {
    const end = new Date(currentDate);
    if (viewType === 'week') {
      end.setDate(end.getDate() + (6 - end.getDay())); // End of week (Saturday)
    } else if (viewType === 'month') {
      end.setMonth(end.getMonth() + 1, 0);
      end.setDate(end.getDate() + (6 - end.getDay()));
    }
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const detectShiftConflicts = (shiftsData) => {
    const conflicts = [];
    
    shiftsData.forEach((shift, index) => {
      // Check for overlapping shifts for same employee
      const overlapping = shiftsData.slice(index + 1).filter(otherShift => 
        otherShift.assigned_employee_id === shift.assigned_employee_id &&
        new Date(shift.start_ts) < new Date(otherShift.end_ts) &&
        new Date(shift.end_ts) > new Date(otherShift.start_ts)
      );
      
      if (overlapping.length > 0) {
        conflicts.push({
          type: 'overlap',
          severity: 'error',
          shifts: [shift, ...overlapping],
          message: `Overlapping shifts for ${shift.first_name} ${shift.last_name}`
        });
      }
      
      // Check for UK Working Time Regulations violations
      if (shift.assigned_employee_id) {
        const employeeShifts = shiftsData.filter(s => 
          s.assigned_employee_id === shift.assigned_employee_id &&
          new Date(s.start_ts).getTime() >= new Date().getTime() - 7 * 24 * 60 * 60 * 1000
        );
        
        const totalWeeklyHours = employeeShifts.reduce((total, s) => {
          const duration = (new Date(s.end_ts) - new Date(s.start_ts)) / (1000 * 60 * 60);
          return total + duration;
        }, 0);
        
        if (totalWeeklyHours > 48) {
          conflicts.push({
            type: 'working_time',
            severity: 'warning',
            shifts: [shift],
            message: `${shift.first_name} ${shift.last_name} exceeds 48 hours/week (${totalWeeklyHours.toFixed(1)}h)`
          });
        }
      }
      
      // Check for insufficient rest periods
      const previousShift = shiftsData.find(s => 
        s.assigned_employee_id === shift.assigned_employee_id &&
        new Date(s.end_ts) <= new Date(shift.start_ts) &&
        (new Date(shift.start_ts) - new Date(s.end_ts)) < 11 * 60 * 60 * 1000 // Less than 11 hours rest
      );
      
      if (previousShift) {
        const restHours = (new Date(shift.start_ts) - new Date(previousShift.end_ts)) / (1000 * 60 * 60);
        conflicts.push({
          type: 'rest_period',
          severity: 'error',
          shifts: [previousShift, shift],
          message: `${shift.first_name} ${shift.last_name} has only ${restHours.toFixed(1)}h rest (min 11h required)`
        });
      }
    });
    
    return conflicts;
  };

  const handleShiftDrop = async (draggedItem, targetDate, targetHour) => {
    try {
      const newStartTime = new Date(targetDate);
      newStartTime.setHours(targetHour, 0, 0, 0);
      
      const duration = draggedItem.type === 'shift' 
        ? new Date(draggedItem.end_ts) - new Date(draggedItem.start_ts)
        : 8 * 60 * 60 * 1000; // Default 8 hours for new shifts
      
      const newEndTime = new Date(newStartTime.getTime() + duration);
      
      const updatedShift = {
        ...draggedItem,
        start_ts: newStartTime.toISOString(),
        end_ts: newEndTime.toISOString()
      };
      
      // Validate the move
      const validation = await ukSchedulingAPI.validateAction({
        action_type: 'assign_shift',
        entity_data: updatedShift
      });
      
      if (!validation.data.compliance_result.is_compliant) {
        onConflictDetected?.(validation.data.compliance_result.violations);
        return;
      }
      
      // Update shift
      if (draggedItem.shift_id) {
        await ukSchedulingAPI.updateShift(draggedItem.shift_id, updatedShift);
      } else {
        await ukSchedulingAPI.createShift(updatedShift);
      }
      
      onShiftUpdate?.(updatedShift);
      fetchCalendarData();
      
    } catch (error) {
      console.error('Error updating shift:', error);
    }
  };

  const handleEmployeeDrop = async (employee, shiftId) => {
    try {
      const validation = await ukSchedulingAPI.validateAction({
        action_type: 'assign_shift',
        entity_data: {
          shift_id: shiftId,
          employee_id: employee.id
        }
      });
      
      if (!validation.data.compliance_result.is_compliant) {
        onConflictDetected?.(validation.data.compliance_result.violations);
        return;
      }
      
      await ukSchedulingAPI.assignShift(shiftId, { employee_id: employee.id });
      fetchCalendarData();
      
    } catch (error) {
      console.error('Error assigning employee:', error);
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const getDateRange = () => {
    const start = getCalendarStartDate();
    const end = getCalendarEndDate();
    
    if (viewType === 'week') {
      return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
  };

  const renderTimeSlots = () => {
    const hours = [];
    for (let i = 6; i <= 22; i++) {
      hours.push(
        <div key={i} className="h-12 border-b border-gray-100 text-xs text-gray-500 p-1">
          {String(i).padStart(2, '0')}:00
        </div>
      );
    }
    return hours;
  };

  const renderCalendarGrid = () => {
    const startDate = getCalendarStartDate();
    const days = [];
    
    for (let i = 0; i < (viewType === 'week' ? 7 : 42); i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      // Skip if month view and date not in current month
      if (viewType === 'month' && date.getMonth() !== currentDate.getMonth()) {
        days.push(
          <div key={i} className="bg-gray-50 opacity-50">
            <DayHeader date={date} isOtherMonth />
            <DayColumn date={date} shifts={[]} />
          </div>
        );
        continue;
      }
      
      const dayShifts = shifts.filter(shift => {
        const shiftDate = new Date(shift.start_ts);
        return shiftDate.toDateString() === date.toDateString();
      });
      
      days.push(
        <div key={i} className="border-r border-gray-200 last:border-r-0">
          <DayHeader date={date} />
          <DayColumn date={date} shifts={dayShifts} />
        </div>
      );
    }
    
    return days;
  };

  const DayHeader = ({ date, isOtherMonth = false }) => (
    <div className={`p-2 text-center border-b border-gray-200 ${
      isOtherMonth ? 'text-gray-400' : 'text-gray-900'
    }`}>
      <div className="text-xs font-medium">
        {date.toLocaleDateString('en-GB', { weekday: 'short' })}
      </div>
      <div className={`text-lg font-semibold ${
        date.toDateString() === new Date().toDateString() 
          ? 'bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto'
          : ''
      }`}>
        {date.getDate()}
      </div>
    </div>
  );

  const DayColumn = ({ date, shifts }) => {
    const [, drop] = useDrop({
      accept: [ItemTypes.SHIFT, ItemTypes.EMPLOYEE],
      drop: (item, monitor) => {
        const targetElement = monitor.getDropResult();
        if (targetElement) {
          const hour = parseInt(targetElement.hour) || 9;
          handleShiftDrop(item, date, hour);
        }
      }
    });

    return (
      <div ref={drop} className="relative" style={{ height: '680px' }}>
        {/* Time slot backgrounds */}
        {Array.from({ length: 17 }, (_, i) => (
          <TimeSlot 
            key={i} 
            hour={i + 6} 
            date={date}
            onDrop={handleShiftDrop}
          />
        ))}
        
        {/* Shifts */}
        {shifts.map(shift => (
          <ShiftBlock 
            key={shift.shift_id}
            shift={shift}
            conflicts={conflicts.filter(c => c.shifts.some(s => s.shift_id === shift.shift_id))}
            onEmployeeDrop={handleEmployeeDrop}
            onClick={() => setSelectedShift(shift)}
          />
        ))}
      </div>
    );
  };

  const TimeSlot = ({ hour, date, onDrop }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: [ItemTypes.SHIFT, ItemTypes.EMPLOYEE],
      drop: (item) => ({ hour: hour.toString(), date }),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop()
      })
    });

    return (
      <div
        ref={drop}
        className={`absolute w-full h-10 border-b border-gray-100 ${
          isOver && canDrop ? 'bg-blue-50' : ''
        }`}
        style={{ top: `${(hour - 6) * 40}px` }}
        data-hour={hour}
        data-date={date.toISOString()}
      />
    );
  };

  const ShiftBlock = ({ shift, conflicts, onEmployeeDrop, onClick }) => {
    const startHour = new Date(shift.start_ts).getHours();
    const startMinute = new Date(shift.start_ts).getMinutes();
    const duration = (new Date(shift.end_ts) - new Date(shift.start_ts)) / (1000 * 60 * 60);
    
    const top = ((startHour - 6) * 40) + (startMinute / 60 * 40);
    const height = duration * 40;
    
    const hasConflicts = conflicts.length > 0;
    const hasError = conflicts.some(c => c.severity === 'error');
    const hasWarning = conflicts.some(c => c.severity === 'warning');

    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.SHIFT,
      item: { ...shift, type: 'shift' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    const [{ isOver }, drop] = useDrop({
      accept: ItemTypes.EMPLOYEE,
      drop: (employee) => onEmployeeDrop(employee, shift.shift_id),
      collect: (monitor) => ({
        isOver: monitor.isOver()
      })
    });

    return (
      <div
        ref={(node) => drag(drop(node))}
        className={`absolute left-1 right-1 rounded p-1 text-xs cursor-pointer border-2 ${
          isDragging ? 'opacity-50' : ''
        } ${
          hasError ? 'bg-red-100 border-red-400 text-red-800' :
          hasWarning ? 'bg-yellow-100 border-yellow-400 text-yellow-800' :
          shift.assigned_employee_id ? 'bg-blue-100 border-blue-400 text-blue-800' :
          'bg-gray-100 border-gray-400 text-gray-800'
        } ${isOver ? 'ring-2 ring-blue-500' : ''}`}
        style={{ top: `${top}px`, height: `${Math.max(height, 20)}px` }}
        onClick={onClick}
        data-testid={`shift-block-${shift.shift_id}`}
      >
        <div className="font-medium truncate">
          {shift.assigned_employee_id ? `${shift.first_name} ${shift.last_name}` : 'Unassigned'}
        </div>
        <div className="text-xs truncate">
          {new Date(shift.start_ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - 
          {new Date(shift.end_ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>
        {shift.site_name && (
          <div className="text-xs truncate opacity-75">
            📍 {shift.site_name}
          </div>
        )}
        {hasConflicts && (
          <div className="flex items-center mt-1">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            <span className="text-xs">{conflicts.length} issue{conflicts.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    );
  };

  const EmployeeList = () => (
    <div data-testid="employee-list" className="w-64 bg-white border-r border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Staff</h3>
      <div className="space-y-2">
        {employees.map(employee => (
          <DraggableEmployee key={employee.id} employee={employee} />
        ))}
      </div>
    </div>
  );

  const DraggableEmployee = ({ employee }) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.EMPLOYEE,
      item: { ...employee, type: 'employee' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    return (
      <div
        ref={drag}
        className={`p-2 bg-gray-50 rounded border cursor-pointer hover:bg-gray-100 ${
          isDragging ? 'opacity-50' : ''
        }`}
        data-testid={`draggable-employee-${employee.id}`}
      >
        <div className="flex items-center">
          <UserIcon className="w-4 h-4 mr-2 text-gray-500" />
          <div>
            <div className="text-sm font-medium">{employee.first_name} {employee.last_name}</div>
            <div className="text-xs text-gray-500">{employee.employee_number}</div>
          </div>
        </div>
      </div>
    );
  };

  const FilterPanel = () => (
    <div data-testid="filter-panel" className={`bg-white border-b border-gray-200 p-4 ${showFilters ? 'block' : 'hidden'}`}>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <select
          value={activeFilters.department}
          onChange={(e) => setActiveFilters({...activeFilters, department: e.target.value})}
          className="form-input-sm"
        >
          <option value="">All Departments</option>
          {/* Add department options */}
        </select>
        
        <select
          value={activeFilters.site}
          onChange={(e) => setActiveFilters({...activeFilters, site: e.target.value})}
          className="form-input-sm"
        >
          <option value="">All Sites</option>
          {sites.map(site => (
            <option key={site.site_id} value={site.site_id}>{site.name}</option>
          ))}
        </select>
        
        <select
          value={activeFilters.role}
          onChange={(e) => setActiveFilters({...activeFilters, role: e.target.value})}
          className="form-input-sm"
        >
          <option value="">All Roles</option>
          {roles.map(role => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </select>
        
        <select
          value={activeFilters.employee}
          onChange={(e) => setActiveFilters({...activeFilters, employee: e.target.value})}
          className="form-input-sm"
        >
          <option value="">All Employees</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
          ))}
        </select>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={activeFilters.showOvertime}
            onChange={(e) => setActiveFilters({...activeFilters, showOvertime: e.target.checked})}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm">Show Overtime</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={activeFilters.showConflicts}
            onChange={(e) => setActiveFilters({...activeFilters, showConflicts: e.target.checked})}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm">Show Conflicts Only</span>
        </label>
      </div>
    </div>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div data-testid="drag-drop-shift-calendar" className="flex flex-col h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateDate(-1)}
                  className="p-2 hover:bg-gray-100 rounded"
                  data-testid="prev-period-button"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold text-gray-900">
                  {getDateRange()}
                </h2>
                <button
                  onClick={() => navigateDate(1)}
                  className="p-2 hover:bg-gray-100 rounded"
                  data-testid="next-period-button"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
              
              <button
                onClick={() => setCurrentDate(new Date())}
                className="btn-outline btn-sm"
                data-testid="today-button"
              >
                Today
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline btn-sm flex items-center"
                data-testid="toggle-filters-button"
              >
                <FunnelIcon className="w-4 h-4 mr-2" />
                Filters
              </button>
              
              {(isAdmin() || isManager()) && (
                <button
                  className="btn-primary btn-sm flex items-center"
                  data-testid="create-shift-button"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Shift
                </button>
              )}
            </div>
          </div>
        </div>

        <FilterPanel />

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          <EmployeeList />
          
          <div className="flex-1 overflow-auto">
            <div className="flex">
              {/* Time column */}
              <div className="w-16 bg-gray-50 border-r border-gray-200">
                <div className="h-16"></div> {/* Header spacer */}
                {renderTimeSlots()}
              </div>
              
              {/* Calendar grid */}
              <div className={`flex-1 grid ${
                viewType === 'week' ? 'grid-cols-7' : 'grid-cols-7'
              }`}>
                {renderCalendarGrid()}
              </div>
            </div>
          </div>
        </div>

        {/* Conflicts Summary */}
        {conflicts.length > 0 && (
          <div data-testid="conflicts-summary" className="bg-yellow-50 border-t border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2" />
                <span className="text-sm font-medium text-yellow-800">
                  {conflicts.length} scheduling conflict{conflicts.length > 1 ? 's' : ''} detected
                </span>
              </div>
              <button
                className="text-sm text-yellow-600 hover:text-yellow-800"
                onClick={() => navigate('/uk-compliance-dashboard')}
              >
                View Details →
              </button>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default DragDropShiftCalendar;